import type {
  AllocationProgress,
  AllocationServerCell,
  AllocationServerGrid,
  AllocationServerScope,
  AllocationServerTeacher,
  VersiServerChange,
} from '../types/allocationServer'
import type {LessonPlanningCell, LessonPlanningData} from '../types/teacherAllocationAi'
import {
  type AllocationCounts,
  type AllocationRow,
  type ProposalChange,
  type ProposalChangeKind,
  slotKeyFor,
} from './teacherAllocationModel'

/**
 * Maps the server-side Teacher Allocation API (handoff §2–§6) onto the shapes the existing
 * allocation components already render (AllocationRow, LessonPlanningData, ProposalChange), so
 * the landing page, grid, cell settings and Versi page serve both flows. Pure: unit tested on
 * its own.
 *
 * Keys: a row is a class (`classId`), a cell a subject row (`subjectId`). The grid components
 * address a cell by `${classKey}|||${subject name}`; the maps below translate both ways.
 */

/** The grid's row key for a server class. */
export const classKeyOf = (classId: number) => `class:${classId}`

/** A grid row key back to its `classId` (null for a key that is not a server class). */
export const classIdOf = (classKey: string): number | null => {
  const match = /^class:(\d+)$/.exec(classKey)
  return match ? Number(match[1]) : null
}

/** A slot key's class key and subject label. */
export const splitSlotKey = (slotKey: string) => {
  const idx = slotKey.lastIndexOf('|||')
  return idx < 0
    ? {classKey: slotKey, subject: ''}
    : {classKey: slotKey.slice(0, idx), subject: slotKey.slice(idx + 3)}
}

/** The server's column key: the lower-cased, trimmed subject name. */
export const columnKeyOf = (subjectName: string) => subjectName.trim().toLowerCase()

export const countsOf = (progress: AllocationProgress | null | undefined): AllocationCounts => ({
  total: progress?.required ?? 0,
  filled: progress?.filled ?? 0,
  remaining: progress?.remaining ?? 0,
})

/** The stored frequency as the cell settings read it: "N/day" when daily, else "N/week". */
export function frequencyOf(
  weeklyPeriods: number | null | undefined,
  dailyPeriods: number | null | undefined
) {
  if (dailyPeriods && dailyPeriods > 0) return `${dailyPeriods}/day`
  if (weeklyPeriods && weeklyPeriods > 0) return `${weeklyPeriods}/week`
  return ''
}

export type AllocationTeacherChoice = {id: number; name: string; employeeId?: string}

export const teacherChoiceOf = (teacher: AllocationServerTeacher): AllocationTeacherChoice => ({
  id: teacher.id,
  name: teacher.name,
  employeeId: teacher.employeeId || undefined,
})

export const lessonCellOf = (cell: AllocationServerCell): LessonPlanningCell => ({
  teacher: cell.state === 'assigned' ? (cell.teacher?.name ?? '') : '',
  teacherEmployeeId: cell.state === 'assigned' ? (cell.teacher?.employeeId ?? '') : '',
  frequency: frequencyOf(cell.weeklyPeriods, cell.dailyPeriods),
  duration: String(cell.consecutivePeriods || 1),
})

export type ServerGridView = {
  rows: AllocationRow[]
  grid: LessonPlanningData
  /** Not-taught cells (slot keys). */
  deleted: Set<string>
  /** Cells still holding the teacher an upload gave (file icon). */
  imported: Set<string>
  /** slot key → subjectId, and back. */
  subjectIdBySlot: Map<string, number>
  slotBySubjectId: Map<number, string>
  cellBySubjectId: Map<number, AllocationServerCell>
  rowByClassKey: Map<string, AllocationRow>
  /** Column key → its label, to address proposal cells by the grid's subject names. */
  columnNames: Map<string, string>
  teachers: AllocationTeacherChoice[]
}

export function buildServerGridView(grid: AllocationServerGrid | null): ServerGridView {
  const view: ServerGridView = {
    rows: [],
    grid: {},
    deleted: new Set(),
    imported: new Set(),
    subjectIdBySlot: new Map(),
    slotBySubjectId: new Map(),
    cellBySubjectId: new Map(),
    rowByClassKey: new Map(),
    columnNames: new Map(),
    teachers: [],
  }
  if (!grid) return view
  grid.columns.forEach((column) => view.columnNames.set(column.key, column.name))
  view.teachers = (grid.teachers ?? []).map(teacherChoiceOf)

  grid.branches.forEach((branch) => {
    branch.rows.forEach((serverRow) => {
      const classKey = classKeyOf(serverRow.classId)
      const subjects: string[] = []
      const cells: Record<string, LessonPlanningCell> = {}
      grid.columns.forEach((column) => {
        const cell = serverRow.cells[column.key]
        if (!cell) return
        subjects.push(column.name)
        cells[column.name] = lessonCellOf(cell)
        const row = {classKey}
        const slot = slotKeyFor(row, column.name)
        view.subjectIdBySlot.set(slot, cell.subjectId)
        view.slotBySubjectId.set(cell.subjectId, slot)
        view.cellBySubjectId.set(cell.subjectId, cell)
        if (cell.state === 'not_taught') view.deleted.add(slot)
        if (cell.source === 'upload' && cell.state === 'assigned') view.imported.add(slot)
      })
      const row: AllocationRow = {
        classKey,
        // `name` stays the stored name (ids, saving); the page shows `displayName`, which drops
        // the curriculum prefix ("Cambridge Stage 8-B" -> "Stage 8-B") unless it is needed.
        className: serverRow.name,
        label: serverRow.displayName || serverRow.name,
        branch: branch.name,
        level: serverRow.levelDisplayName || serverRow.level || '',
        curriculum: '',
        grade: '',
        section: '',
        subjects,
      }
      view.rows.push(row)
      view.rowByClassKey.set(classKey, row)
      view.grid[classKey] = cells
    })
  })
  return view
}

/**
 * The grid after an edit: each returned cell replaces its own by `subjectId`. Edit responses
 * carry `source: null`; the file icon stays only while the teacher is the one the upload gave.
 */
export function mergeServerCells(
  grid: AllocationServerGrid,
  cells: AllocationServerCell[]
): AllocationServerGrid {
  if (cells.length === 0) return grid
  const byId = new Map(cells.map((cell) => [cell.subjectId, cell]))
  let changed = false
  const branches = grid.branches.map((branch) => {
    let branchChanged = false
    const rows = branch.rows.map((row) => {
      let rowChanged = false
      const nextCells: Record<string, AllocationServerCell> = {}
      Object.entries(row.cells).forEach(([key, cell]) => {
        const next = byId.get(cell.subjectId)
        if (!next) {
          nextCells[key] = cell
          return
        }
        rowChanged = true
        const keepsSource = cell.source === 'upload' && cell.teacherId === next.teacherId
        nextCells[key] = {
          ...next,
          source: keepsSource ? 'upload' : null,
          matches: grid.filter === 'all' || next.state === grid.filter,
        }
      })
      if (!rowChanged) return row
      branchChanged = true
      return {...row, cells: nextCells}
    })
    if (!branchChanged) return branch
    changed = true
    return {...branch, rows}
  })
  return changed ? {...grid, branches} : grid
}

/** One cell, re-stated locally before the server answers (an optimistic teacher pick). */
export function withCellTeacher(
  cell: AllocationServerCell,
  teacher: AllocationServerTeacher | null
): AllocationServerCell {
  return {
    ...cell,
    state: teacher ? 'assigned' : 'unassigned',
    teacherId: teacher?.id ?? null,
    teacher,
  }
}

// --- Branch / Level dropdowns ------------------------------------------------------------

export type ServerScopeChoices = {
  branch: string
  level: string
  branches: {values: string[]; hasAll: boolean}
  levels: {values: string[]; hasAll: boolean}
  branchLabel: (value: string) => string
  levelLabel: (value: string) => string
}

/**
 * The dropdowns as the server resolved them (§3.1): values are ids as strings, `''` is All.
 * Level names repeat across branches; clashing ones are labelled "Primary · North Campus".
 */
export function scopeChoicesOf(
  scope: AllocationServerScope | null | undefined
): ServerScopeChoices {
  const branchOptions = scope?.branchOptions ?? []
  const levelOptions = scope?.levelOptions ?? []
  const branchNames = new Map(branchOptions.map((o) => [String(o.id), o.name]))
  // The level as the page shows it (no curriculum prefix unless needed); older servers: `name`.
  const shown = (o: (typeof levelOptions)[number]) => o.displayName || o.name
  const nameCounts = new Map<string, number>()
  levelOptions.forEach((o) => nameCounts.set(shown(o), (nameCounts.get(shown(o)) ?? 0) + 1))
  const levelNames = new Map(
    levelOptions.map((o) => {
      const clash = (nameCounts.get(shown(o)) ?? 0) > 1
      const branch = branchNames.get(String(o.branchId))
      return [String(o.id), clash && branch ? `${shown(o)} · ${branch}` : shown(o)]
    })
  )
  return {
    branch: scope?.branchId == null ? '' : String(scope.branchId),
    level: scope?.levelId == null ? '' : String(scope.levelId),
    branches: {
      values: branchOptions.map((o) => String(o.id)),
      hasAll: Boolean(scope?.branchHasAll),
    },
    levels: {values: levelOptions.map((o) => String(o.id)), hasAll: Boolean(scope?.levelHasAll)},
    branchLabel: (value) => branchNames.get(value) ?? value,
    levelLabel: (value) => levelNames.get(value) ?? value,
  }
}

/** A dropdown value back to the API's id (`''` / All → null). */
export const scopeIdOf = (value: string): number | null => {
  const n = Number(value)
  return value !== '' && Number.isFinite(n) ? n : null
}

// --- Versi proposals ------------------------------------------------------------------------

const KIND_BY_TYPE: Record<VersiServerChange['type'], ProposalChangeKind> = {
  new: 'new',
  replace: 'replace',
  clear: 'unassign',
  weekly: 'frequency',
  consecutive: 'duration',
  not_taught: 'remove',
}

/**
 * A proposal's changes as the Versi page and the grid preview show them. `columnNames` puts the
 * cell under the grid's own subject label when the grid is loaded.
 */
export function proposalChangesOf(
  changes: VersiServerChange[],
  columnNames?: ReadonlyMap<string, string>
): ProposalChange[] {
  return changes.map((change) => {
    const subject =
      columnNames?.get(columnKeyOf(change.cell.subjectName)) ?? change.cell.subjectName
    const classKey = classKeyOf(change.cell.classId)
    const kind = KIND_BY_TYPE[change.type] ?? 'new'
    let current = ''
    let proposed = ''
    let weekly: Pick<ProposalChange, 'currentWeekly' | 'proposedWeekly'> = {}
    if (kind === 'frequency') {
      current = frequencyOf(change.current.weeklyPeriods, change.current.dailyPeriods)
      proposed = frequencyOf(change.proposed.weeklyPeriods, change.proposed.dailyPeriods)
      weekly = {
        currentWeekly: change.current.weeklyPeriods ?? null,
        proposedWeekly: change.proposed.weeklyPeriods ?? null,
      }
    } else if (kind === 'duration') {
      current = String(change.current.consecutivePeriods || 1)
      proposed = String(change.proposed.consecutivePeriods || 1)
    } else {
      current = change.current.teacher ?? ''
      proposed = kind === 'remove' ? '' : (change.proposed.teacher ?? '')
    }
    return {
      slotKey: slotKeyFor({classKey}, subject),
      classKey,
      // The label when the class is outside the loaded grid (another level or branch).
      className: change.cell.className,
      subject,
      kind,
      current,
      proposed,
      ...weekly,
    }
  })
}

/** Rows for the classes a proposal names, so the Versi page can label them before the grid loads. */
export function proposalRowsOf(changes: VersiServerChange[]): AllocationRow[] {
  const rows = new Map<string, AllocationRow>()
  changes.forEach(({cell}) => {
    const classKey = classKeyOf(cell.classId)
    if (rows.has(classKey)) return
    rows.set(classKey, {
      classKey,
      className: cell.className,
      label: cell.className,
      branch: cell.branch,
      level: cell.level ?? '',
      curriculum: '',
      grade: '',
      section: '',
      subjects: [],
    })
  })
  return Array.from(rows.values())
}
